import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { megabytes } from '../lib/format';

type Exists = (key: string) => boolean;

/**
 * Where each list of machine-readable reason codes is translated. Every
 * `error.details.reason` (user management, APK upload, Zoho test ticket) shares
 * `errors.reason`: those lists never clash. The Zoho results outside an error
 * (test connection, last test, alerts) use the same Zoho words, so the same group.
 */
export const REASON_GROUPS = {
  /** `error.details.reason`: USER_ADMIN_REASONS, APK_UPLOAD_REASONS, ZOHO_FAILURE_REASONS (and the sign-in reasons). */
  error: 'errors.reason',
  /** `reasonCode` of a Zoho result, `lastTestReasonCode`, `zohoUpdateErrorReasonCode`: ZOHO_FAILURE_REASONS. */
  zoho: 'errors.reason',
  /** `reasonCode` of the check-endpoint result: ENDPOINT_CHECK_REASONS. */
  endpoint: 'distribution.endpointReason',
  /** `rows[].reasonCode` of the bulk-import report: BULK_IMPORT_ROW_REASONS. */
  importRow: 'import.reason',
} as const;
export type ReasonGroup = keyof typeof REASON_GROUPS;

export type ReasonParams = Record<string, unknown>;

/**
 * The translated text of a reason code, with its params interpolated. An absent
 * code, or one this build has no words for (a newer server), gives `fallback`,
 * or else the generic message carrying the code so it can still be reported.
 * Never the server's English text: that is technical detail, shown apart.
 */
export function reasonText(
  t: TFunction,
  exists: Exists,
  group: ReasonGroup,
  code: string | null | undefined,
  params: ReasonParams = {},
  fallback?: string,
): string {
  if (code) {
    const key = `${REASON_GROUPS[group]}.${code}`;
    if (exists(key)) return t(key, params);
  }
  return fallback ?? t('errors.unknown', { code: code || '—' });
}

/**
 * Interpolation values from an error's `details`: `permissions` (an array of
 * permission keys) as their translated names, joined; `maxBytes` as `maxMb`.
 */
export function detailParams(details: unknown, t: TFunction, exists: Exists): ReasonParams {
  const d = (details ?? {}) as { permissions?: unknown; maxBytes?: unknown };
  const params: ReasonParams = {};
  if (Array.isArray(d.permissions)) {
    params.permissions = d.permissions
      .filter((p): p is string => typeof p === 'string')
      .map((p) => (exists(`users.permission.${p}`) ? t(`users.permission.${p}`) : p))
      .join(t('bulk.listSeparator'));
  }
  if (typeof d.maxBytes === 'number') params.maxMb = megabytes(d.maxBytes);
  return params;
}

/** `reasonText` bound to the current language. */
export function useReasonText(): (group: ReasonGroup, code: string | null | undefined, params?: ReasonParams, fallback?: string) => string {
  const { t, i18n } = useTranslation();
  return useCallback(
    (group: ReasonGroup, code: string | null | undefined, params?: ReasonParams, fallback?: string) =>
      reasonText(t, (key) => i18n.exists(key), group, code, params, fallback),
    [t, i18n],
  );
}
